import {
  Badge,
  BadgeVariant,
  spacingHelper,
  type LinkDangerouslySetClassNames,
  type LinkIconPlacement,
  type LinkProps,
} from '@property.com.au/ui-kit';
import type { AnchorHTMLAttributes, FunctionComponent } from 'react';
import styled from 'styled-components';
import { ClickThroughSourceLocalStorageValue } from '../../../analytics/click-through-tracking/TrackingSource';
import { OutboundTrackingEventData } from '../../../analytics/event-tracking/trackOutboundLinkClick';
import { ExternalLink } from '../ExternalLink/ExternalLink';
import { InternalLink } from '../InternalLink/InternalLink';
import { LinkIconName } from '../types';

const StyledBadge = styled(Badge)`
  margin-left: ${spacingHelper('extraSmall')};
  align-self: center;
  height: ${spacingHelper('medium')};
`;

export interface IconInfo {
  name: LinkIconName;
  placement?: LinkIconPlacement | null;
}

interface Callout {
  text: string;
  variant: BadgeVariant;
}

export interface WebLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'rel' | 'className'> {
  title: string;
  url: string;
  isExternal: boolean;
  variant: LinkProps['variant'];
  iconInfo?: IconInfo | null;
  callout?: Callout | null;
  tracking?: {
    outboundTracking?: OutboundTrackingEventData | null;
    clickThroughTracking?: ClickThroughSourceLocalStorageValue | null;
  } | null;
  key?: string;
  className?: string;
  sizeVariant?: LinkProps['sizeVariant'];
  dangerouslySetClassNames?: LinkDangerouslySetClassNames;
  rel?: string | null;
  onClick?: () => void;
  fullWidth?: boolean;
  disabled?: boolean | null;
  clientSideRouting?: boolean;
}

export const WebLink: FunctionComponent<WebLinkProps> = ({
  title,
  isExternal,
  url,
  variant,
  tracking,
  rel,
  callout,
  disabled,
  ...rest
}) => {
  return isExternal ? (
    <ExternalLink
      key={url}
      href={url}
      linkVariant={variant}
      {...(rel && { rel })}
      outboundLinkType={tracking?.outboundTracking?.data.type || ''}
      schema={tracking?.outboundTracking?.schema}
      event_name={tracking?.outboundTracking?.data.event_name}
      name={tracking?.outboundTracking?.data.name}
      context={tracking?.outboundTracking?.data.context}
      disabled={disabled ?? undefined}
      {...rest}
    >
      {title}
    </ExternalLink>
  ) : (
    <InternalLink
      key={url}
      to={url}
      linkVariant={variant}
      clickThroughTracking={tracking?.clickThroughTracking}
      rel={rel || undefined}
      disabled={disabled ?? undefined}
      {...rest}
    >
      {title}
      {callout && (
        <StyledBadge variant={callout.variant} sizeVariant="extraSmall">
          {callout.text}
        </StyledBadge>
      )}
    </InternalLink>
  );
};
